// Flurfotos (seit 30.10.2026 nach der Flurskizze, Betreiber: „KI nur für Möbel und Volumen“). Form, Ecken, Abzweige und
// Türen hat der Kunde im Schritt davor selbst gezeichnet (Grundriss.tsx); hier fotografiert er den Flur nur noch für die
// Möbel (Garderobe, Kommode, Schuhschrank …).
//
// Damit der Kunde nicht scrollt und scrollt: je Wand eine Reihe mit Piktogramm, die Fotos dieser Wand nebeneinander,
// die Kamera als letzte Kachel. Reihenfolge je Abschnitt: Blick hinein, linke Wand, rechte Wand; am Ende zurück zum
// Eingang. Diele: einmal ringsum. Gespeichert wie bisher (photos.step, photos.hall_section).
//
// Ältere Aufnahmen (Flurfotos vor der Skizze) zeigen ihre Fotos weiter in denselben Reihen.

import { useEffect, useState } from "react";
import { hallSketchSvg, sectionOrder, type HallSection, type PhotoStep } from "@umzugsscan/shared";
import { entranceHall } from "../ablauf.ts";
import { useNav } from "../Erfassung.tsx";
import { useDraft, useStore } from "../hooks.ts";
import { qualityIssue } from "../ui/Bild.tsx";
import { Button, Dialog, Footer } from "../ui/elemente.tsx";
import { Fotohinweis } from "../ui/Fotohinweis.tsx";
import { Kamera } from "../ui/Kamera.tsx";
import { FotoReihe } from "../ui/Galerie.tsx";
import type { Photo } from "../zustand.ts";

type Art = "blick" | "ecke" | "links" | "rechts" | "zurueck" | "ringsum" | "abzweig";

/** Kleines Piktogramm je Reihe: Gang von unten nach oben, die gemeinte Wand bzw. Blickrichtung hervorgehoben. */
function Pikto(props: { art: Art }) {
  const a = props.art;
  return (
    <svg viewBox="0 0 44 44" width="44" height="44" aria-hidden="true" className="reihen-pikto">
      {a === "ringsum" ? (
        <>
          <rect x="8" y="8" width="28" height="28" rx="2" className="p-gang" />
          <path d="M22 14 a8 8 0 1 1 -7 4" className="p-pfeil" />
          <path d="M13 13 l2 5 l4 -3" className="p-pfeil" />
        </>
      ) : (
        <>
          <rect x="15" y="4" width="14" height="36" className="p-gang" />
          {a === "links" && <line x1="15" y1="5" x2="15" y2="39" className="p-wand" />}
          {a === "rechts" && <line x1="29" y1="5" x2="29" y2="39" className="p-wand" />}
          {a === "ecke" && <path d="M22 36 V20 H38 M33 15 L39 20 L33 25" className="p-pfeil" />}
          {a === "blick" && <path d="M22 36 V10 M17 15 L22 9 L27 15" className="p-pfeil" />}
          {a === "zurueck" && <path d="M22 9 V35 M17 30 L22 36 L27 30" className="p-pfeil" />}
          {a === "abzweig" && <path d="M22 36 V20 H38 M33 15 L39 20 L33 25" className="p-pfeil" />}
        </>
      )}
    </svg>
  );
}

const TEXT: Record<Art, { titel: string; hinweis: string }> = {
  blick: { titel: "Blick in den Flur", hinweis: "Rücken zur Wohnungstür, ein Foto den Flur entlang." },
  ecke: { titel: "Um die Ecke", hinweis: "An der Ecke stehen, ein Foto in den neuen Teil des Flurs hinein." },
  links: { titel: "Linke Wand", hinweis: "Von der rechten Seite aus, Stück für Stück. Fotos dürfen sich überschneiden." },
  rechts: { titel: "Rechte Wand", hinweis: "Von der linken Seite aus, Stück für Stück." },
  zurueck: { titel: "Zurück zum Eingang", hinweis: "Am Ende umdrehen, ein Foto Richtung Wohnungstür." },
  ringsum: { titel: "Diele ringsum", hinweis: "Vom Eingang aus jede Wand, bis Sie wieder am Eingang sind." },
  abzweig: { titel: "In den Abzweig hinein", hinweis: "Am Abzweig stehen, ein Foto hinein." },
};

export function Eingang() {
  const d = useDraft();
  const store = useStore();
  const { weiter, zurueck } = useNav();
  const hall = entranceHall(d.rooms);
  const [busy, setBusy] = useState(false);
  const [leer, setLeer] = useState(false);

  useEffect(() => {
    if (d.survey.photoNotice) store.ensureEntranceHall();
  }, [d.survey.photoNotice, store]);

  if (!d.survey.photoNotice) return <Fotohinweis onBack={zurueck} />;
  if (!hall) return null;

  const hallPhotos = d.photos.filter((p) => p.roomId === hall.id);
  const sec = (p: Photo) => p.section ?? 1;
  const of = (step: PhotoStep, nr: number) => hallPhotos.filter((p) => p.step === step && sec(p) === nr);
  const issues = hallPhotos.filter((p) => qualityIssue(p)).length;
  const shape = hall.hallShape ?? "gerade";
  // Seit 31.10.2026 ein Baum (Grundriss.tsx): Reihen in der Reihenfolge zum Ablaufen, je Gang ein Abschnitt.
  const sections = sectionOrder(hall.hallSections);
  const lastMain = sections.filter((s) => s.art !== "abzweig").at(-1)!;
  const titel = (s: HallSection) =>
    s.art === "start"
      ? shape === "diele"
        ? "Diele"
        : "Vom Eingang"
      : s.art === "ecke"
        ? `Um die Ecke nach ${s.richtung ?? "?"}`
        : shape === "diele" && s.von === 1
          ? `Gang aus der Diele ${s.richtung === "geradeaus" ? "geradeaus" : `nach ${s.richtung ?? "?"}`}`
          : `Abzweig nach ${s.richtung ?? "?"}`;
  const doors = d.rooms.filter((r) => r.hallRoomId === hall.id).sort((a, b) => (a.hallOrder ?? 0) - (b.hallOrder ?? 0));

  const reihe = (art: Art, step: PhotoStep, nr: number, single = false) => (
    <div className="wand-reihe" key={`${art}-${nr}`}>
      <div className="wand-reihe-kopf">
        <Pikto art={art} />
        <div>
          <strong>{TEXT[art].titel}</strong>
          <span>{TEXT[art].hinweis}</span>
        </div>
      </div>
      <div className="wand-reihe-fotos">
        <FotoReihe reihe photos={of(step, nr)} onDelete={(p) => store.deletePhoto(p.id)} />
        {!(single && of(step, nr).length > 0) && <Kamera kompakt roomId={hall.id} step={step} section={nr} single={single} onBusy={setBusy} />}
      </div>
    </div>
  );
  const abschnitt = (s: HallSection, titel: string | null, rows: React.ReactNode) => (
    <section className="card abschnitt-fotos" key={s.nr}>
      {titel && (
        <div className="abschnitt-kopf">
          <span className="abschnitt-nr">{s.nr}</span>
          <h2>{titel}</h2>
        </div>
      )}
      {rows}
    </section>
  );

  const next = () => {
    if (hallPhotos.length === 0) return setLeer(true);
    store.confirmRoom(hall.id);
    weiter();
  };

  return (
    <>
      <p className="eyebrow">Flur fotografieren</p>
      <h1>Jetzt Fotos vom Flur</h1>
      <p className="lead">Damit wir Garderobe, Schränke und alles andere im Flur sehen. Jede Reihe ist eine Wand – tippen Sie auf 📷 und fotografieren Sie sie.</p>
      <p className="hint zoom">Tipp: Kamera auf <strong>0,5×</strong> (Weitwinkel), dann passt mehr aufs Bild.</p>

      {doors.length > 0 && (
        <details className="skizze-klein">
          <summary>Ihre Flurskizze ansehen</summary>
          <div
            className="flurskizze-rahmen"
            dangerouslySetInnerHTML={{
              __html: hallSketchSvg(
                hall.hallSections,
                doors.map((r, i) => ({ label: r.name, section: r.hallSection ?? 1, side: r.hallSide ?? null, order: r.hallOrder ?? i, pos: r.hallPos ?? null, nr: String(i + 1) })),
              ),
            }}
          />
        </details>
      )}

      {sections.map((s) =>
        abschnitt(
          s,
          sections.length > 1 ? titel(s) : null,
          s.art === "start" && shape === "diele" ? (
            reihe("ringsum", "rundum", 1)
          ) : (
            <>
              {s.art === "start" ? reihe("blick", "tuer", 1, true) : s.art === "ecke" ? reihe("ecke", "flur_knick", s.nr, true) : reihe("abzweig", "flur_knick", s.nr, true)}
              {/* Wohnungstür direkt an der Ecke: kein Gang geradeaus, also keine Wände und kein Blick zurück. */}
              {!s.kurz && reihe("links", "flur_links", s.nr)}
              {!s.kurz && reihe("rechts", "flur_rechts", s.nr)}
              {s.nr === lastMain.nr && !s.kurz && shape !== "diele" && reihe("zurueck", "flur_zurueck", s.nr, true)}
            </>
          ),
        ),
      )}
      {hallPhotos.some((p) => p.step === "flur" || (shape !== "diele" && p.step === "rundum")) && (
        <section className="card">
          <h2>Weitere Flurfotos</h2>
          <FotoReihe photos={hallPhotos.filter((p) => p.step === "flur" || (shape !== "diele" && p.step === "rundum"))} onDelete={(p) => store.deletePhoto(p.id)} />
        </section>
      )}
      {issues > 0 && <p className="hint">{issues} Foto{issues === 1 ? "" : "s"} bitte neu aufnehmen (× löschen, dann neu fotografieren).</p>}

      <Footer onBack={zurueck} onNext={next} nextDisabled={busy} nextLabel="Weiter zu den Räumen" />

      {leer && (
        <Dialog
          title="Noch kein Flurfoto"
          onClose={() => setLeer(false)}
          actions={
            <>
              <Button onClick={() => setLeer(false)}>Foto aufnehmen</Button>
              <Button
                variant="quiet"
                onClick={() => {
                  setLeer(false);
                  store.confirmRoom(hall.id);
                  weiter();
                }}
              >
                Im Flur steht nichts – weiter
              </Button>
            </>
          }
        >
          <p>Ohne Fotos sehen wir keine Möbel im Flur. Steht dort nichts, können Sie auch so weiter.</p>
        </Dialog>
      )}
    </>
  );
}
